import Intercom, { update } from '@intercom/messenger-js-sdk';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router';

// Contexts
import MobileContext from 'Shared/contexts/MobileContext';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useDOMObserver } from 'Shared/utils/hooks/use-dom-observer';
import { useApp } from 'Shared/utils/hooks/use-app';

type Props = {
  children: React.ReactNode;
};

const IntercomWrapper = ({ children }: Props) => {
  const { user } = useCurrentUser();
  const location = useLocation();
  const isMobile = useContext(MobileContext);
  const { isZoom, intercomAppId } = useApp();
  const isCommentsPanelOpened = location.pathname.includes('comments');
  const shouldHideIntercom = isMobile || isCommentsPanelOpened || isZoom;

  const getIntercomWrapper = () => document.getElementById('intercom-container');
  const getIntercomStatus = () => Boolean(getIntercomWrapper());
  const [intercomIsActive, setIntercomIsActive] = useState(getIntercomStatus());

  const handleActiveIntercom = useCallback(() => setIntercomIsActive(getIntercomStatus()), [location]);

  const initilizeIntercom = () => {
    Intercom({
      app_id: intercomAppId,
      alignment: 'right',
      z_index: 1040,
      email: user.email,
      name: user.full_name,
      user_id: user.pi_user_guid,
    });
  };

  useDOMObserver({ callback: handleActiveIntercom });

  useEffect(() => {
    initilizeIntercom();
  }, []);

  // Updates the location in Intercom when the user navigates since we're in a single page app
  useEffect(() => {
    update({});
  }, [location]);

  useLayoutEffect(() => {
    const intercomWrapper = getIntercomWrapper();
    if (intercomIsActive && shouldHideIntercom) intercomWrapper.style.setProperty('display', 'none', 'important');
    if (intercomIsActive && !shouldHideIntercom) intercomWrapper.style.display = 'block';
  }, [isMobile, shouldHideIntercom, intercomIsActive, location]);

  return <>{children}</>;
};

export default IntercomWrapper;
